import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ReembolsoService } from '@/services/reembolsoService';
import { 
  CheckCircle, 
  Calendar, 
  Building2, 
  Hash, 
  DollarSign,
  FileText,
  X
} from "lucide-react";

interface ComprovantePix {
  id_transacao_pix: string;
  data_transferencia: string;
  banco_origem?: string;
  banco_destino?: string;
  valor_transferido: number;
  codigo_operacao?: string;
  comprovante_url?: string;
  observacoes_admin?: string;
}

interface ReembolsoDetalhes {
  id: string;
  valor: number;
  motivo: string;
  status: string;
  created_at: string;
  data_processamento?: string;
  observacao_admin?: string;
  comprovante_pix?: string; // JSON string
  cpf_chave_pix: string;
}

interface ModalVisualizarReembolsoProps {
  isOpen: boolean;
  onClose: () => void;
  reembolso: ReembolsoDetalhes | null;
}

const ModalVisualizarReembolso: React.FC<ModalVisualizarReembolsoProps> = ({
  isOpen,
  onClose,
  reembolso
}) => {
  if (!reembolso) return null;

  // Parse do comprovante PIX se existir
  let comprovantePix: ComprovantePix | null = null;
  if (reembolso.comprovante_pix) {
    try {
      comprovantePix = JSON.parse(reembolso.comprovante_pix);
    } catch (error) {
      // ignorar erros de parse do comprovante PIX
    }
  }

  const formatarData = (data: string) => {
    return new Date(data).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PROCESSADO': return 'bg-green-100 text-green-800';
      case 'APROVADO': return 'bg-blue-100 text-blue-800';
      case 'PENDENTE': return 'bg-yellow-100 text-yellow-800';
      case 'REJEITADO': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <CheckCircle className="w-6 h-6 text-green-600" />
            Detalhes do Reembolso
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Status e Informações Básicas */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-lg">Informações Gerais</h3>
                <Badge className={getStatusColor(reembolso.status)}>
                  {reembolso.status === 'PROCESSADO' ? 'Processado' : reembolso.status}
                </Badge>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500 font-medium">Valor Solicitado:</span>
                  <p className="text-lg font-semibold text-green-600">
                    {ReembolsoService.formatarValor(reembolso.valor)}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500 font-medium">Data da Solicitação:</span>
                  <p className="font-medium">{formatarData(reembolso.created_at)}</p>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-500 font-medium">Motivo:</span>
                  <p className="mt-1">{reembolso.motivo}</p>
                </div>
                <div>
                  <span className="text-gray-500 font-medium">Chave PIX (CPF):</span>
                  <p className="font-mono">{ReembolsoService.formatarCPF(reembolso.cpf_chave_pix)}</p>
                </div>
                {reembolso.data_processamento && (
                  <div>
                    <span className="text-gray-500 font-medium">Data do Processamento:</span>
                    <p className="font-medium">{formatarData(reembolso.data_processamento)}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Detalhes da Transferência PIX */}
          {comprovantePix && (
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  Comprovante da Transferência PIX
                </h3>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Hash className="w-4 h-4 text-gray-400" />
                    <div>
                      <span className="text-gray-500 font-medium">ID da Transação:</span>
                      <p className="font-mono text-xs">{comprovantePix.id_transacao_pix}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <div>
                      <span className="text-gray-500 font-medium">Data/Hora:</span>
                      <p className="font-medium">{formatarData(comprovantePix.data_transferencia)}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-gray-400" />
                    <div>
                      <span className="text-gray-500 font-medium">Banco de Origem:</span>
                      <p className="font-medium">{comprovantePix.banco_origem || 'Não informado'}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-gray-400" />
                    <div>
                      <span className="text-gray-500 font-medium">Valor Transferido:</span>
                      <p className="font-semibold text-green-600">
                        {ReembolsoService.formatarValor(comprovantePix.valor_transferido)}
                      </p>
                    </div>
                  </div>
                  
                  {comprovantePix.codigo_operacao && (
                    <div className="col-span-2 flex items-center gap-2">
                      <Hash className="w-4 h-4 text-gray-400" />
                      <div>
                        <span className="text-gray-500 font-medium">Código da Operação:</span>
                        <p className="font-mono">{comprovantePix.codigo_operacao}</p>
                      </div>
                    </div>
                  )}
                </div>
                
                {comprovantePix.observacoes_admin && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-4 h-4 text-blue-600" />
                      <span className="text-blue-800 font-medium text-sm">Observações da Administração:</span>
                    </div>
                    <p className="text-blue-700 text-sm">{comprovantePix.observacoes_admin}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Botão Fechar */}
          <div className="flex justify-end pt-4">
            <Button
              onClick={onClose}
              variant="outline"
              className="flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              Fechar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ModalVisualizarReembolso; 